import { type ProveedorRequest, type ProveedorFormErrors } from '../types/proveedor';

export interface ValidationRule<T> {
  validate: (value: T) => string | undefined;
}

export interface ValidationRules {
  [key: string]: ValidationRule<unknown>[];
}

export class ProveedorValidationService {
  private static readonly EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  private static readonly validationRules: ValidationRules = {
    razonSocial: [
      {
        validate: (value: unknown) => {
          if (!value || typeof value !== 'string' || !value.trim()) {
            return 'Razón Social es requerida';
          }
          if (value.trim().length > 200) {
            return 'Razón Social no puede exceder 200 caracteres';
          }
          return undefined;
        },
      },
    ],
    identificacion: [
      {
        validate: (value: unknown) => {
          if (!value || typeof value !== 'string' || !value.trim()) {
            return 'Identificación es requerida';
          }
          if (value.trim().length > 20) {
            return 'Identificación no puede exceder 20 caracteres';
          }
          return undefined;
        },
      },
    ],
    idTipoIdentificacion: [
      {
        validate: (value: unknown) => {
          if (!value || typeof value !== 'number') {
            return 'Tipo de Identificación es requerido';
          }
          return undefined;
        },
      },
    ],
    idGenero: [
      {
        validate: (value: unknown) => {
          if (!value || typeof value !== 'number') {
            return 'Género es requerido';
          }
          return undefined;
        },
      },
    ],
    correoContacto: [
      {
        validate: (value: unknown) => {
          if (value && typeof value === 'string' && value.trim()) {
            if (!this.EMAIL_REGEX.test(value.trim())) {
              return 'Email no es válido';
            }
            if (value.trim().length > 100) {
              return 'Email no puede exceder 100 caracteres';
            }
          }
          return undefined;
        },
      },
    ],
    telefono: [
      {
        validate: (value: unknown) => {
          if (value && typeof value === 'string' && value.trim() && value.trim().length > 100) {
            return 'Teléfono no puede exceder 100 caracteres';
          }
          return undefined;
        },
      },
    ],
  };

  public static validateProveedor(proveedor: ProveedorRequest): ProveedorFormErrors {
    const errors: ProveedorFormErrors = {};

    Object.entries(this.validationRules).forEach(([field, rules]) => {
      const fieldValue = proveedor[field as keyof ProveedorRequest];
      const fieldError = this.validateField(fieldValue, rules);
      if (fieldError) {
        errors[field as keyof ProveedorFormErrors] = fieldError;
      }
    });

    return errors;
  }

  public static validateField(value: unknown, rules: ValidationRule<unknown>[]): string | undefined {
    for (const rule of rules) {
      const error = rule.validate(value);
      if (error) {
        return error;
      }
    }
    return undefined;
  }

  public static isFormValid(errors: ProveedorFormErrors): boolean {
    return Object.keys(errors).length === 0;
  }

  public static getValidationRules(): ValidationRules {
    return this.validationRules;
  }
}